import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { api } from '../api';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { BlogSubscribe } from '../components/BlogSubscribe';
import { toast } from 'sonner';
import { ChevronRight, ArrowRight, Loader2, Share2, Copy, PenSquare } from 'lucide-react';

// ----------------------------------------------------------------------
// BlogPost — public reader at /blog/:slug
// ----------------------------------------------------------------------
// Fetches the published post from the backend, renders a keyword-safe
// Markdown → HTML preview, and injects per-post <title>, meta
// description, canonical, OG/Twitter tags, and Article JSON-LD.
// ----------------------------------------------------------------------

const SITE = 'https://k9os.app';

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
};
const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Small, safe Markdown → HTML converter (headings, bold/italic/code,
// links, bullet lists, paragraphs). Escapes content first so nothing
// pasted in the editor can inject arbitrary HTML.
const renderMarkdown = (md) => {
  if (!md) return '';
  const lines = md.split(/\r?\n/);
  const out = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul class="list-disc pl-6 my-4 space-y-1.5 text-slate-700 dark:text-slate-200">'); inList = true; }
      out.push(`<li>${esc(line.replace(/^\s*[-*]\s+/, ''))}</li>`);
      continue;
    }
    if (inList) { out.push('</ul>'); inList = false; }
    if (/^###\s+/.test(line)) { out.push(`<h3 class="mt-6 mb-2 font-display text-xl font-bold text-[#0B1220] dark:text-slate-50">${esc(line.replace(/^###\s+/, ''))}</h3>`); continue; }
    if (/^##\s+/.test(line))  { out.push(`<h2 class="mt-8 mb-3 font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50">${esc(line.replace(/^##\s+/, ''))}</h2>`); continue; }
    if (/^#\s+/.test(line))   { out.push(`<h2 class="mt-8 mb-3 font-display text-2xl font-bold text-[#0B1220] dark:text-slate-50">${esc(line.replace(/^#\s+/, ''))}</h2>`); continue; }
    if (!line.trim()) { out.push(''); continue; }
    let html = esc(line);
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[13px]">$1</code>');
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#1B3A87] dark:text-[#93C5FD] underline hover:text-[#C8102E] dark:hover:text-[#F87171]" target="_blank" rel="noopener noreferrer">$1</a>');
    out.push(`<p class="my-4 leading-relaxed text-slate-700 dark:text-slate-200">${html}</p>`);
  }
  if (inList) out.push('</ul>');
  return out.join('\n');
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } catch { return ''; }
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await api.get(`/blog/${slug}`);
        if (alive) setPost(data);
      } catch (e) {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const canonical = `${SITE}/blog/${slug}`;

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    const title = post.seo_title || post.title;
    const desc = post.seo_description || post.excerpt || '';
    document.title = title;
    setMeta('description', desc);
    setMeta('keywords', (post.keywords || []).join(', '));
    setMeta('og:title', title, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:type', 'article', 'property');
    if (post.cover_image) setMeta('og:image', post.cover_image, 'property');
    setMeta('twitter:title', title);
    setMeta('twitter:description', desc);
    if (post.cover_image) setMeta('twitter:image', post.cover_image);
    setCanonical(canonical);

    upsertJsonLd('k9os-blog-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Article',
      '@id': `${canonical}#article`,
      mainEntityOfPage: canonical,
      headline: title,
      description: desc,
      keywords: (post.keywords || []).join(', '),
      image: post.cover_image ? [post.cover_image] : undefined,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      author: { '@type': 'Person', name: post.author || 'K9os Team' },
      publisher: { '@id': 'https://k9os.app/#organization' },
      isPartOf: { '@id': 'https://k9os.app/#website' },
      articleSection: (post.tags || []).join(', ') || 'Dog Training Software',
    });

    return () => {
      document.title = prevTitle;
      const el = document.getElementById('k9os-blog-jsonld');
      if (el) el.remove();
    };
  }, [post, canonical]);

  const share = useMemo(() => {
    const u = encodeURIComponent(canonical);
    const t = encodeURIComponent(post?.seo_title || post?.title || '');
    return {
      x: `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
    };
  }, [canonical, post]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(canonical); toast.success('Link copied'); } catch { toast.error('Copy failed'); }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-white dark:bg-slate-950'>
        <PublicNav />
        <div className='py-32 flex items-center justify-center text-slate-500 gap-2'>
          <Loader2 className='w-5 h-5 animate-spin' /> Loading post…
        </div>
      </div>
    );
  }
  if (notFound || !post) {
    return <Navigate to='/blog' replace />;
  }

  return (
    <div className='min-h-screen bg-white dark:bg-slate-950' data-testid='blog-post'>
      <PublicNav />

      {/* Breadcrumb */}
      <div className='border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900'>
        <nav aria-label='Breadcrumb' className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1'>
          <Link to='/' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Home</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <Link to='/blog' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Blog</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <span className='text-slate-900 dark:text-slate-100 font-medium truncate max-w-[240px]'>{post.title}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className='py-10 md:py-14 bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 border-b border-slate-200 dark:border-slate-800'>
        <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-xs uppercase tracking-[0.18em] font-bold text-[#C8102E] flex items-center gap-2'>
            <PenSquare className='w-3.5 h-3.5' /> K9os Blog · {formatDate(post.published_at)}
          </div>
          <h1 className='mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight' data-testid='post-title'>
            {post.title}
          </h1>
          {post.excerpt && <p className='mt-4 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed'>{post.excerpt}</p>}
          {(post.keywords || []).length > 0 && (
            <ul className='mt-5 flex flex-wrap gap-2'>
              {(post.keywords || []).slice(0, 8).map((k, i) => (
                <li key={i} className='px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-[#1B3A87] dark:text-[#93C5FD]'>{k}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Cover */}
      {post.cover_image && (
        <div className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 mb-6'>
          <img src={post.cover_image} alt={post.title} className='w-full rounded-2xl border border-slate-200 dark:border-slate-800 shadow-md' loading='eager' />
        </div>
      )}

      {/* Body */}
      <article className='max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-14'>
        <div
          className='text-slate-800 dark:text-slate-100'
          dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content_markdown) }}
          data-testid='post-body'
        />

        {/* CTA */}
        <div className='mt-10 rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-r from-[#1B3A87]/5 via-white to-[#C8102E]/5 dark:from-[#1B3A87]/20 dark:via-slate-900 dark:to-[#C8102E]/20 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4'>
          <div className='flex-1'>
            <h3 className='font-display text-lg md:text-xl font-bold text-[#0B1220] dark:text-slate-50'>Run your dog training business on one system.</h3>
            <p className='text-sm md:text-base text-slate-600 dark:text-slate-300 mt-1'>Book a free K9os App demo tailored to your training company.</p>
          </div>
          <Link
            to='/contact'
            className='inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white text-sm font-bold shadow-lg'
            data-testid='post-cta-demo'
          >
            Book a Free Demo <ArrowRight className='w-4 h-4' />
          </Link>
        </div>

        {/* Subscribe — capture the reader while intent is highest. */}
        <div className='mt-8'>
          <BlogSubscribe source={`post:${slug}`} compact />
        </div>

        {/* Share */}
        <div className='mt-10 pt-6 border-t border-slate-200 dark:border-slate-800'>
          <div className='flex items-center gap-3 flex-wrap'>
            <div className='text-xs uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5'>
              <Share2 className='w-3.5 h-3.5' /> Share this post
            </div>
            <a href={share.x} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='post-share-x'>X</a>
            <a href={share.facebook} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='post-share-facebook'>Facebook</a>
            <a href={share.linkedin} target='_blank' rel='noopener noreferrer' className='text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87]' data-testid='post-share-linkedin'>LinkedIn</a>
            <button onClick={copyUrl} className='text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-800 hover:border-[#1B3A87] inline-flex items-center gap-1.5' data-testid='post-share-copy'><Copy className='w-3 h-3' /> Copy link</button>
          </div>
        </div>

        <div className='mt-8'>
          <Link to='/blog' className='text-sm font-semibold text-[#1B3A87] dark:text-[#93C5FD] hover:text-[#C8102E] inline-flex items-center gap-1'>
            <ChevronRight className='w-3 h-3 rotate-180' /> Back to all posts
          </Link>
        </div>
      </article>

      <MessageGregBubble />
    </div>
  );
}
